<template>
  <v-app-bar flat :color="isScrolled || drawer ? 'white' : 'transparent'"
    :style="drawer ? 'border-bottom: 1px solid white;' : ''" class="hero-app-bar">
    <v-app-bar-nav-icon @click="drawer = !drawer" v-if="isMobileWidth"
      :color="isScrolled || drawer ? '#555555' : '#FFFFFF'" />
    <v-spacer></v-spacer>
    <div v-if="!isMobileWidth" class="d-flex">
      <v-btn v-for="item in menuItems" :key="item.id" text :ripple="false" class="nav-btn"
        :class="isScrolled ? 'text-grey-darken-3' : 'text-white'" @click="scrollTo(item.id)">{{ item.label }}</v-btn>
    </div>
    <v-select v-model="currentLang" :items="languages" hide-details density="compact" variant="outlined"
      style="max-width: 90px; min-width: 80px;" item-title="short" item-value="value"
      :menu-props="{ offsetX: 0, offsetY: 0, contentClass: 'custom-select-menu' }">

      <template #selection>
        <div class="flex items-center gap-1">
          <v-img :src="selectedLanguage.flag" alt="flag" style="width: 18px; height: 12px; border-radius: 2px;" />
        </div>
      </template>

      <template #item="{ item, props }">
        <v-list-item :active="props.active" @click="props.onClick" class="compact-list-item">
          <template #prepend>
            <v-img :src="item.raw.flag" alt="flag" style="width: 20px; height: 14px; border-radius: 2px;" />
          </template>
          <v-list-item-title class="ml-1" style="font-size: 0.8rem;">
            {{ item.raw.short }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-select>
  </v-app-bar>

  <v-navigation-drawer v-if="isMobileWidth" v-model="drawer" temporary left>
    <v-list>
      <v-list-item v-for="item in menuItems" :key="item.id" @click="scrollTo(item.id); drawer = false">
        <v-list-item-title class="nav-item">{{ item.label }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <!-- Hero Image -->
  <div id="hero" class="hero-section" :style="heroStyle">
    <v-container class="fill-height d-flex flex-column justify-center align-center text-white text-center">
      <h1 class="display-2 font-weight-bold">Sea Gem</h1>
      <h2>Privlaka</h2>
      <h3>{{ $t('hero.country') }}</h3>
    </v-container>
  </div>

  <!-- About Section -->
  <v-container id="about" class="my-12">
    <h2 class="mb-8 text-center">{{ $t('about.title') }}</h2>
    <v-row>
      <v-col cols="12" md="6">
        <p class="text-justify">
          {{ $t('about.text') }}
        </p>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-center align-center">
        <img src="https://res.cloudinary.com/di8h1fsel/image/upload/w_600,h_400,c_fill,q_auto,f_auto/surrounding/103.jpg"
          class="photo-about" loading="lazy" />
      </v-col>
    </v-row>
  </v-container>

  <!-- Gallery Preview -->
  <v-container id="gallery" class="my-12">
    <h2 class="mb-8 text-center">{{ $t('gallery.title') }}</h2>
    <v-slide-group>
      <v-slide-group-item v-for="(img, i) in mainPictures" :key="i">
        <img :src="`https://res.cloudinary.com/di8h1fsel/image/upload/w_400,h_300,c_fill,q_auto,f_auto/${img}.jpg`"
          :width="isMobile ? '80vw' : 200" :height="isMobile ? 'auto' : 150" loading="lazy"
          class="ma-2 rounded-lg cursor-pointer" @click="openImage(img)" :alt="`Gallery image ${img}`" />
      </v-slide-group-item>
    </v-slide-group>

    <v-dialog v-model="dialog" max-width="800">
      <v-card>
        <v-img :src="`https://res.cloudinary.com/di8h1fsel/image/upload/w_1200,q_auto,f_auto/${selected}.jpg`"
          :lazy-src="`https://res.cloudinary.com/di8h1fsel/image/upload/w_100,h_75,q_auto,f_auto/${selected}.jpg`"
          aspect-ratio="16/9">
        </v-img>
      </v-card>
    </v-dialog>
    <div class="text-center mt-4">
      <v-btn color="primary" to="/gallery">
        {{ $t('gallery.more') }}
      </v-btn>
    </div>
  </v-container>

  <!-- Facilities -->
  <v-container id="facilities" class="my-12">
    <h2 class="mb-8 text-center">{{ $t('facilities.title') }}</h2>
    <v-row>
      <v-col cols="6" md="3" v-for="facility in facilities" :key="facility.name" class="text-center">
        <v-icon large>{{ facility.icon }}</v-icon>
        <div>{{ facility.name }}</div>
      </v-col>
    </v-row>
  </v-container>

  <!-- Reviews Slider -->
  <v-container id="reviews" class="my-12">
    <h2 class="mb-8 text-center">{{ $t('reviews.title') }}</h2>
    <v-carousel cycle hide-delimiter-background height="200">
      <v-carousel-item v-for="(review, i) in reviews" :key="i">
        <div class="text-center">
          <p class="font-italic">"{{ review.text }}"</p>
          <strong>- {{ review.author }}</strong>
        </div>
      </v-carousel-item>
    </v-carousel>
  </v-container>

  <!-- Map and Contact -->
  <v-container id="contact" class="my-12">
    <h2 class="mmb-8 text-center">{{ $t('contact.title') }}</h2>
    <v-row>
      <v-col cols="12" md="6" class="mb-6 mb-md-0">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2856.625935967234!2d15.125222683366383!3d44.276514700000014!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47621f7646b645d9%3A0xa1bcd9ab6b431a4!2sPut%20Skoblara%20141%2C%2023233%2C%20Privlaka%2C%20Chorwacja!5e0!3m2!1spl!2spl!4v1758810292343!5m2!1spl!2spl"
          width="100%" height="300" frameborder="0" allowfullscreen loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"></iframe>
        <p class="mt-2"><strong>{{ $t('contact.address') }}</strong> Privlaka, Put Skoblara 141, {{ $t('hero.country') }}
        </p>
        <p><strong>{{ $t('contact.phone') }}</strong> <i class="fab fa-whatsapp" style="color:#25D366; margin:6px;" aria-hidden="true"></i><a href="https://wa.me/385952022221">00385952022221</a></p>
        <p><strong>{{ $t('contact.email') }}</strong> <a href="mailto:info@loopzadar.hr">info@loopzadar.hr</a></p>
      </v-col>

      <v-col v-if="!mailSent" cols="12" md="6">
        <v-form @submit.prevent="sendEmail">
          <v-text-field :label="$t('contact.form.name')" v-model="form.name" required></v-text-field>
          <v-text-field label="Email" v-model="form.email" required></v-text-field>
          <v-textarea :label="$t('contact.form.message')" v-model="form.message" required></v-textarea>
          <p class="error-msg">{{ errorMessage }}</p>

          <v-btn color="primary" type="submit" :disabled="loadingEmail">
            <template v-if="loadingEmail">
              <v-progress-circular indeterminate size="20" color="white" class="mr-2"></v-progress-circular>
              {{ $t('contact.form.sending') }}
            </template>
            <template v-else>
              {{ $t('contact.form.send') }}
            </template>
          </v-btn>
        </v-form>
      </v-col>
      <v-col v-else cols="12" md="6" class="mb-6 mb-md-0 sent">
        <v-icon color="green" size="48">mdi-check-circle</v-icon>
        <h3>{{ $t('contact.successTitle') }}</h3>
        <p>{{ $t('contact.successMsg') }}</p>
        <v-btn color="primary" class="mt-4" @click="mailSent = false">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('gallery.back') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { useHead } from '@vueuse/head'
export default {
  name: 'HomeView',
  data() {
    return {
      windowWidth: window.innerWidth,
      facilities: [
        { name: this.$t('facilities.pool'), icon: "mdi-pool" },
        { name: this.$t('facilities.wifi'), icon: "mdi-wifi" },
        { name: this.$t('facilities.ac'), icon: "mdi-air-conditioner" },
        { name: this.$t('facilities.parking'), icon: "mdi-parking" },
        { name: this.$t('facilities.sea_view'), icon: "mdi-water" },
        { name: this.$t('facilities.private_beach'), icon: "mdi-beach" },
        { name: this.$t('facilities.bbq'), icon: "mdi-fire" },
        { name: this.$t('facilities.dishwasher'), icon: "mdi-dishwasher" },
        { name: this.$t('facilities.washing_machine'), icon: "mdi-washing-machine" },
        { name: this.$t('facilities.smart_tv'), icon: "mdi-television" },
        { name: this.$t('facilities.garden'), icon: "mdi-flower" },
        { name: this.$t('facilities.balcony'), icon: "mdi-balcony" },
      ],
      form: {
        name: "",
        email: "",
        message: "",
      },
      mainPictures: ["pool/7", "livingroom/18", "pool/21", "pool/25", "livingroom/32",
        "pool/67", "pool/94", "pool/122"],
      selected: null,
      dialog: false,
      isScrolled: false,
      drawer: false,
      errorMessage: "",
      mailSent: false,
      languages: [
        { label: 'English', value: 'en', short: 'EN', flag: 'https://res.cloudinary.com/di8h1fsel/image/upload/v1759652543/countries/gb.png' },
        { label: 'Polski', value: 'pl', short: 'PL', flag: 'https://res.cloudinary.com/di8h1fsel/image/upload/v1759652543/countries/pl.png' },
        { label: 'Hrvatski', value: 'hr', short: 'HR', flag: 'https://res.cloudinary.com/di8h1fsel/image/upload/v1759652543/countries/hr.png' },
        { label: 'Deutsch', value: 'de', short: 'DE', flag: 'https://res.cloudinary.com/di8h1fsel/image/upload/v1761379813/countries/de.png' },
      ],
      currentLang: 'en',
      isMobile: false,
      loadingEmail: false,
    };
  },
  watch: {
    currentLang(newLang) {
      this.$i18n.locale = newLang;
    }
  },
  computed: {
    seoTitle() {
      switch (this.currentLang) {
        case 'pl': return 'Sea Gem - luksusowa willa przy plaży w Privlaka, Chorwacja'
        case 'de': return 'Sea Gem - luxuriöse Villa am Strand in Privlaka, Kroatien'
        case 'hr': return 'Sea Gem - luksuzna vila na plaži u Privlaki, Hrvatska'
        default: return 'Sea Gem - luxury villa at the beach in Privlaka, Croatia'
      }
    },
    seoDescription() {
      switch (this.currentLang) {
        case 'pl': return 'Sea Gem to nowoczesna, luksusowa willa tuż przy plaży w Privlaka, niedaleko Zadaru, z prywatnym basenem, klimatyzowanymi sypialniami i widokiem na morze.'
        case 'de': return 'Sea Gem ist eine moderne, luxuriöse Villa direkt am Strand in Privlaka, nahe Zadar, mit Privatpool, klimatisierten Schlafzimmern und Meerblick.'
        case 'hr': return 'Sea Gem je moderna, luksuzna vila smještena izravno na slikovitoj plaži u Privlaki, blizu Zadra, s privatnim bazenom, klimatiziranim spavaćim sobama i pogledom na more.'
        default: return 'Sea Gem is a modern luxury villa directly on the beach in Privlaka, near Zadar, with private pool, air-conditioned bedrooms, and sea view.'
      }
    },
    seoOgLocale() {
      switch (this.currentLang) {
        case 'pl': return 'pl_PL'
        case 'de': return 'de_DE'
        case 'hr': return 'hr_HR'
        default: return 'en_US'
      }
    },
    heroStyle() {
      const size = this.isMobile ? 'w_768' : 'w_1920';
      return {
        background: `url('https://res.cloudinary.com/di8h1fsel/image/upload/${size},q_auto,f_auto/hero.jpg') center/cover no-repeat`
      }
    },
    menuItems() {
      return [
        { id: 'about', label: this.$t('menu.about') },
        { id: 'gallery', label: this.$t('menu.gallery') },
        { id: 'facilities', label: this.$t('menu.facilities') },
        { id: 'reviews', label: this.$t('menu.reviews') },
        { id: 'contact', label: this.$t('menu.contact') },
      ]
    },
    reviews() {
      return [
        { text: this.$t('reviews.r1'), author: "Lukas" },
        { text: this.$t('reviews.r2'), author: "Emily" },
        { text: this.$t('reviews.r3'), author: "Agnieszka i Paweł" },
        { text: this.$t('reviews.r4'), author: "Hannah" },
        { text: this.$t('reviews.r5'), author: "Joanna" },
        { text: this.$t('reviews.r6'), author: "Danijela" },
        { text: this.$t('reviews.r7'), author: "Gabriela" },
      ]
    },
    isMobileWidth() {
      return this.windowWidth <= 799;
    },
    selectedLanguage() {
      return this.languages.find(l => l.value === this.currentLang) || this.languages[0];
    }
  },

  mounted() {
    useHead({
      title: this.seoTitle,
      meta: [
        { name: 'description', content: this.seoDescription },
        { name: 'robots', content: 'index, follow' },
        { property: 'og:title', content: this.seoTitle },
        { property: 'og:description', content: this.seoDescription },
        { property: 'og:type', content: 'website' },
        { property: 'og:locale', content: this.seoOgLocale }
      ],
    })
    this.checkMobile();
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.updateWindowWidth);
    this.updateWindowWidth();
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.updateWindowWidth);
  },

  methods: {
    preloadNeighborImages(index) {
      const neighbors = [
        this.mainPictures[index - 1],
        this.mainPictures[index + 1]
      ].filter(Boolean);

      neighbors.forEach(img => {
        const image = new Image();
        image.src = `https://res.cloudinary.com/di8h1fsel/image/upload/w_1200,q_auto,f_auto/${img}.jpg`;
      });
    },
    updateWindowWidth() {
      this.isMobile = window.innerWidth <= 768;
      this.windowWidth = window.innerWidth;
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleScroll() {
      const heroHeight = document.getElementById('hero')?.offsetHeight || 0;
      this.isScrolled = window.scrollY > heroHeight;
    },

    scrollTo(id) {
      if (id === 'about') {
        const hero = document.getElementById('hero');
        const heroBottom = hero?.getBoundingClientRect().bottom + window.pageYOffset;
        window.scrollTo({ top: heroBottom, behavior: 'smooth' });
        return;
      }

      const el = document.getElementById(id);
      const offset = document.querySelector('.v-app-bar')?.offsetHeight || 0;
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    },

    openImage(img) {
      const index = this.mainPictures.indexOf(img);
      this.selected = img;
      this.dialog = true;
      this.preloadNeighborImages(index);

    },

    async sendEmail() {
      const { name, email, message } = this.form;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!name || !email || !message) {
        this.errorMessage = this.$t('error.required')
        return;
      }

      if (!emailRegex.test(email)) {
        this.errorMessage = this.$t('error.invalidEmail')
        return;
      }

      this.loadingEmail = true;
      this.errorMessage = "";
      try {
        const res = await fetch(`${process.env.VUE_APP_API_URL}/api/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, message }),
        });

        if (res.status === 200) {
          this.mailSent = true;
          this.form = { name: "", email: "", message: "" };
        } else {
          this.errorMessage = this.$t('error.error');
        }
      } catch (e) {
        this.errorMessage = this.$t('error.error');
      } finally {
        this.loadingEmail = false;
      }
    },
  },
};
</script>


<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Lexend+Tera&display=swap");


.hero-section {
  height: 100vh;
  width: 100%;
  position: relative;
  font-family: "Lexend Tera", sans-serif;
}

.hero-section h1 {
  margin-top: -150px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
}

.hero-section h2 {
  font-weight: 400;
  font-size: 1.5rem;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
}

.hero-section h3 {
  font-weight: 400;
  font-size: 1rem;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
}

.hero-app-bar {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 10;
}

.hero-app-bar .nav-btn {
  margin-right: 20px;
  font-family: "Lexend Tera", sans-serif;
  transition: color 0.3s ease;
  background-color: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.hero-app-bar .nav-btn:hover {
  color: #5ab2ff !important;
}

.nav-item {
  font-family: "Lexend Tera", sans-serif;
  color: #555555;

}

.photo-about {
  max-width: 100%;
  max-height: 400px;
  object-fit: cover;
}

.text-justify {
  text-align: justify;
}

.error-msg {
  color: red;
}

.sent {
  text-align: center;

  p {
    margin-top: 10px;
  }
}

.custom-select-menu {
  left: auto !important;
  right: 0 !important;
  transform: none !important;
}

.compact-list-item {
  min-height: 40px !important;
}

a {
  text-decoration: none;
  color: black
}

a:hover {
  color: rgb(24, 103, 192)
}

@media (max-width: 768px) {
  #gallery .v-slide-group__content {
    justify-content: center;
  }
}
</style>